import type { ImportJobSummary, ImportState } from '@api/lib/api-types';
import { api, ApiError } from '../api';

export interface ProgressCallbacks {
  importId: string;
  onDone: (summary: ImportJobSummary) => void;
  onError: (err: ApiError | Error) => void;
  onCancelled: (summary: ImportJobSummary) => void;
}

const TERMINAL: ReadonlySet<ImportState> = new Set(['completed', 'partial', 'failed', 'cancelled']);

export function renderProgress(host: HTMLElement, cb: ProgressCallbacks): void {
  const card = document.createElement('section');
  card.className = 'card';

  const h2 = document.createElement('h2');
  h2.textContent = 'Importing your notes…';

  const status = document.createElement('p');
  status.className = 'status-line';
  status.textContent = 'Waiting for the worker to pick up your job…';

  const bar = document.createElement('div');
  bar.className = 'progress';
  bar.setAttribute('role', 'progressbar');
  bar.setAttribute('aria-valuemin', '0');
  bar.setAttribute('aria-valuemax', '100');
  bar.setAttribute('aria-valuenow', '0');
  const barInner = document.createElement('div');
  barInner.className = 'progress-bar';
  barInner.style.width = '0%';
  bar.append(barInner);

  const counts = document.createElement('p');
  counts.className = 'small muted';
  counts.textContent = 'Imported 0 / 0 (0 failed)';

  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.className = 'btn btn-secondary';
  cancelBtn.textContent = 'Cancel import';

  card.append(h2, status, bar, counts, cancelBtn);
  host.replaceChildren(card);

  let stopped = false;
  const pollCtl = new AbortController();

  cancelBtn.addEventListener('click', async () => {
    const sure = window.confirm('Cancel the import? Any notes already imported will stay in OneNote.');
    if (!sure) return;
    cancelBtn.disabled = true;
    cancelBtn.textContent = 'Cancelling…';
    try {
      await api.cancelImport(cb.importId);
      // We don't terminate the loop here — let the next status poll surface the
      // canonical 'cancelled' state from the server.
    } catch (err) {
      cancelBtn.disabled = false;
      cancelBtn.textContent = 'Cancel import';
      cb.onError(err as Error);
    }
  });

  function paint(s: ImportJobSummary) {
    const total = s.totalNotes || 0;
    const done = s.importedNotes || 0;
    const failed = s.failedNotes || 0;
    const pct = total > 0 ? Math.min(100, Math.floor(((done + failed) / total) * 100)) : 0;
    barInner.style.width = `${pct}%`;
    bar.setAttribute('aria-valuenow', String(pct));
    counts.textContent = total > 0
      ? `Imported ${done} / ${total} (${failed} failed)`
      : `Imported ${done} (${failed} failed)`;
    status.textContent = humanState(s);
  }

  async function loop() {
    let consecutiveTimeouts = 0;
    while (!stopped) {
      try {
        const s = await api.importStatus(cb.importId, pollCtl.signal);
        consecutiveTimeouts = 0;
        paint(s);
        if (TERMINAL.has(s.state)) {
          stopped = true;
          if (s.state === 'cancelled') cb.onCancelled(s);
          else cb.onDone(s);
          return;
        }
        // 200 OK but not terminal → poll again immediately (server batched).
      } catch (err) {
        const apiErr = err as ApiError;
        if (apiErr?.code === 'TIMEOUT') {
          consecutiveTimeouts += 1;
          if (consecutiveTimeouts >= 6) {
            // ~150s of silence — surface it but keep trying.
            status.textContent = "Still working… we're waiting for an update from the server.";
          }
          continue;
        }
        if (apiErr?.code === 'ABORTED') return;
        if (apiErr?.code === 'NOT_AUTHENTICATED' || apiErr?.action === 'signin') {
          stopped = true;
          cb.onError(apiErr);
          return;
        }
        // Transient — back off briefly and retry.
        await new Promise<void>((r) => setTimeout(r, 2_000));
      }
    }
  }

  void loop();
}

function humanState(s: ImportJobSummary): string {
  switch (s.state) {
    case 'queued':      return 'Queued — your job is waiting to start.';
    case 'validating':  return 'Validating your export…';
    case 'in_progress': return 'Importing notes to OneNote…';
    case 'completed':   return 'All done.';
    case 'partial':     return `Finished with ${s.failedNotes} note(s) failed.`;
    case 'failed':      return s.errorMessage || 'Import failed.';
    case 'cancelled':   return 'Import cancelled.';
    default:            return 'Working…';
  }
}
